import {
  Add16Regular,
  ArrowHookDownRight16Regular,
  ArrowRedo16Regular,
  ArrowUndo16Regular,
  Bookmark16Regular,
  ChevronDown12Regular,
  ClipboardCheckmark16Regular,
  Delete16Regular,
  Flag16Regular,
  GanttChart16Regular,
  Link16Regular,
  MoreHorizontal16Regular,
  PanelRight16Regular,
  Subtract16Regular,
  TableSimple16Regular,
  TextIndentDecrease16Regular,
  TextIndentIncrease16Regular,
} from "@fluentui/react-icons";
import { useRef, useState } from "react";
import { saveText } from "@/desktop/file-io";
import { usePlanSurface } from "@/features/access/use-plan-access";
import { canWorkspace, useWorkspaceRole } from "@/features/access/workspace-access";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AppDialog, DialogActionsBar } from "@/components/app-dialog";
import { BaselineManageDialog, BaselineMenuLabel, BaselineSaveDialog } from "@/features/toolbar/BaselineDialogs";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { DateField } from "@/components/date-field";
import { ZOOM_PERCENT_OPTIONS, zoomPercent, type ZoomPreset } from "@/features/gantt/zoom";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { exportScheduleExcel } from "@/features/schedule/export-schedule-excel";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/date-format";
import { displayBaselineName, modKey } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import { useSettingsHub } from "@/shell/settings-hub";
import { useProjectStore } from "@/store/project-store";

/** 시트 탭(SegmentedControl, fontSizeBase200)과 같은 12px / 16px 줄간격. rem은 13px라 픽셀로 고정. */
const toolbarLabel = "text-[12px] leading-[16px] font-normal";

export function Toolbar() {
  const navigate = useNavigate();
  const project = useProjectStore((s) => s.project);
  const ui = useProjectStore((s) => s.ui);
  const selection = useProjectStore((s) => s.selection);
  const canUndo = useProjectStore((s) => s.past.length > 0);
  const canRedo = useProjectStore((s) => s.future.length > 0);
  const checkpoints = useProjectStore((s) => s.checkpoints);
  const s = useProjectStore.getState;
  const [cpDialog, setCpDialog] = useState<"save" | "restore" | null>(null);
  const [blDialog, setBlDialog] = useState(false);
  const [blManage, setBlManage] = useState(false);
  const [zoomMenuOpen, setZoomMenuOpen] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const focus = selection[0] ?? null;
  const pct = zoomPercent(ui.pxPerDay, ui.zoomPreset);
  const surface = usePlanSurface();
  const allowCreate = canWorkspace(useWorkspaceRole(), "plan.create");

  return (
    <div className="flex h-[44px] min-h-[44px] shrink-0 items-center border-b border-border bg-card px-3">
      <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto scrollbar-none">
        <SegmentedControl
        value={ui.scheduleView}
        onChange={(scheduleView) => s().setUI({ scheduleView })}
        ariaLabel="일정 보기"
        options={[
          { value: "gantt", label: "간트", icon: <GanttChart16Regular /> },
          { value: "sheet", label: "시트", icon: <TableSimple16Regular /> },
        ]}
      />

      <div className="mx-0.5 h-4 w-px bg-border/60 shrink-0" />

      {/* 1. 진척 기준일과 업데이트 */}
      <div className={cn("flex h-8 items-center gap-1.5 rounded-lg bg-muted/60 p-0.5 pl-2 shrink-0", toolbarLabel)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help font-normal text-muted-foreground select-none">기준일</span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-64">
            계획과 실제 진척을 비교할 시점입니다. 선택한 날짜까지의 진행 상황과 지연을 계산합니다.
          </TooltipContent>
        </Tooltip>
        {surface.editSchedule ? (
          <DateField
            value={project.statusDate}
            onChange={(v) => v && s().setProject({ statusDate: v })}
            className="w-[10.5rem]"
            aria-label="진척 기준일"
          />
        ) : (
          <span className="px-1 text-foreground">{formatDate(project.statusDate, "full")}</span>
        )}
        {surface.editProgress && (
          <>
            <div className="h-4 w-px bg-border/60" aria-hidden />
            <button
              type="button"
              aria-label="진척 업데이트 열기"
              className={cn("flex h-7 items-center gap-1 rounded-[6px] px-2 text-foreground/80 outline-none transition-colors hover:bg-card hover:text-foreground hover:shadow-xs focus-visible:ring-2 focus-visible:ring-ring/50", toolbarLabel)}
              onClick={() => s().openDialog("status-update")}
            >
              <ClipboardCheckmark16Regular />
              <span>업데이트…</span>
            </button>
          </>
        )}
      </div>

      {surface.undo && (
        <>
          <div className="mx-0.5 h-4 w-px bg-border/60 shrink-0" />
          <ToolbarGroup>
            <TB icon={<ArrowUndo16Regular />} label="실행 취소" shortcut={`${modKey}Z`} disabled={!canUndo} onClick={() => s().undo()} />
            <TB icon={<ArrowRedo16Regular />} label="다시 실행" shortcut={`⇧${modKey}Z`} disabled={!canRedo} onClick={() => s().redo()} />
          </ToolbarGroup>
        </>
      )}

      {surface.editWbs && (
        <>
          <div className="mx-0.5 h-4 w-px bg-border/60 shrink-0" />
          <ToolbarGroup>
            <TB icon={<Add16Regular />} label="작업 추가" shortcut={`${modKey}↩`} onClick={() => s().addSibling(focus)} />
            <TB icon={<ArrowHookDownRight16Regular />} label="하위 작업" shortcut={`⇧${modKey}↩`} disabled={!focus} onClick={() => focus && s().addChild(focus)} />
            <TB icon={<TextIndentIncrease16Regular />} label="들여쓰기" shortcut="⇥" disabled={!selection.length} onClick={() => s().indent(selection)} />
            <TB icon={<TextIndentDecrease16Regular />} label="내어쓰기" shortcut="⇧⇥" disabled={!selection.length} onClick={() => s().outdent(selection)} />
            <TB icon={<Link16Regular />} label="선택 작업 연결 (FS)" disabled={selection.length < 2} onClick={() => s().linkSelection()} />
            <TB icon={<Delete16Regular />} label="삭제" shortcut={`${modKey}⌫`} disabled={!selection.length} onClick={() => s().requestDeleteTasks(selection)} />
          </ToolbarGroup>
        </>
      )}

      <div className="mx-0.5 h-4 w-px bg-border/60 shrink-0" />

      {/* 4. 간트 전용 줌 */}
      {ui.scheduleView === "gantt" && (
        <>
          <div
            className={cn(
              "flex h-8 items-center rounded-lg bg-muted/60 p-0.5 transition-[box-shadow,background-color] shrink-0",
              zoomMenuOpen && "bg-card shadow-[0_0_0_1.5px_var(--tint)]",
            )}
          >
            <button
              type="button"
              className="flex size-7 items-center justify-center rounded-[6px] text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
              onClick={() => s().nudgeZoom(-1)}
              title={`축소 (${modKey}-)`}
              aria-label="축소"
            >
              <Subtract16Regular />
            </button>
            <DropdownMenu open={zoomMenuOpen} onOpenChange={setZoomMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "inline-flex h-7 min-w-[3.25rem] items-center justify-center gap-0.5 rounded-[6px] px-1 tabular outline-none transition-colors",
                    toolbarLabel,
                    zoomMenuOpen ? "bg-tint/15 text-foreground" : "text-foreground hover:bg-card",
                  )}
                  title="배율 선택"
                  aria-label={`배율 ${pct}%`}
                >
                  {pct}%
                  <ChevronDown12Regular className="text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="min-w-[4.5rem] p-1">
                {ZOOM_PERCENT_OPTIONS.map((p) => (
                  <DropdownMenuItem
                    key={p}
                    className={cn("justify-center tabular", pct === p && "bg-muted")}
                    onClick={() => s().setZoomPercent(p)}
                  >
                    {p}%
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="justify-center text-caption"
                  onClick={() => s().requestFit(selection.length ? "selection" : "project")}
                >
                  맞춤 ({modKey}0)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              type="button"
              className="flex size-7 items-center justify-center rounded-[6px] text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
              onClick={() => s().nudgeZoom(1)}
              title={`확대 (${modKey}=)`}
              aria-label="확대"
            >
              <Add16Regular />
            </button>
          </div>

          {/* 5. 줌 프리셋 (일 / 주 / 월) */}
          <div className="flex h-8 items-center rounded-lg bg-muted/60 p-0.5 shrink-0">
            {(["day", "week", "month"] as ZoomPreset[]).map((z, i) => (
              <button
                key={z}
                type="button"
                onClick={() => s().setZoomPreset(z)}
                className={cn(
                  "h-7 rounded-[6px] px-2.5 transition-all",
                  toolbarLabel,
                  ui.zoomPreset === z
                    ? "bg-card text-foreground shadow-xs font-normal"
                    : "text-muted-foreground hover:bg-card/50 hover:text-foreground",
                )}
                title={`${modKey}${i + 1}`}
              >
                {z === "day" ? "일" : z === "week" ? "주" : "월"}
              </button>
            ))}
          </div>

          <div className="mx-0.5 h-4 w-px bg-border/60 shrink-0" />
        </>
      )}

      {/* 기준선과 체크포인트 */}
      <ToolbarGroup>
        {/* baseline */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn("flex h-7 items-center gap-1 rounded-[6px] px-2 text-foreground/80 transition-colors hover:bg-card hover:text-foreground hover:shadow-xs", toolbarLabel)}
            >
              <Flag16Regular />
              <span className="max-w-[70px] truncate">
                {displayBaselineName(project.baselines.find((b) => b.id === project.activeBaselineId)?.name)}
              </span>
              <ChevronDown12Regular className="text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            {surface.createBaseline && <DropdownMenuItem onClick={() => setBlDialog(true)}>현재 일정을 기준선으로 저장…</DropdownMenuItem>}
            {ui.scheduleView === "gantt" && (
              <DropdownMenuCheckboxItem checked={ui.showBaseline} onCheckedChange={(v) => s().setUI({ showBaseline: !!v })}>
                간트에 기준선 표시
              </DropdownMenuCheckboxItem>
            )}
            {project.baselines.length > 0 && surface.approveBaseline && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>비교 기준선</DropdownMenuLabel>
                <DropdownMenuRadioGroup value={project.activeBaselineId ?? ""} onValueChange={(v) => s().setActiveBaseline(v || null)}>
                  {project.baselines.map((b) => (
                    <DropdownMenuRadioItem
                      key={b.id}
                      value={b.id}
                      className="min-w-0"
                      aria-label={[displayBaselineName(b.name), formatDate(b.createdAt, "dateTime"), b.note].filter(Boolean).join(", ")}
                    >
                      <BaselineMenuLabel baseline={b} />
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setBlManage(true)}>기준선 관리…</DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* checkpoint */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn("flex h-7 items-center gap-1 rounded-[6px] px-2 text-foreground/80 transition-colors hover:bg-card hover:text-foreground hover:shadow-xs", toolbarLabel)}
            >
              <Bookmark16Regular />
              <span>체크포인트</span>
              <ChevronDown12Regular className="text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            {(surface.editWbs || surface.editSchedule) && (
              <>
                <DropdownMenuItem onClick={() => setCpDialog("save")}>지금 상태를 체크포인트로 저장…</DropdownMenuItem>
                <DropdownMenuItem disabled={!checkpoints.length} onClick={() => setCpDialog("restore")}>
                  체크포인트에서 복원…
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem
              disabled={!checkpoints.length}
              onClick={() => {
                const pid = s().activeId;
                if (pid && checkpoints[0]) navigate(`/projects/${pid}/reports?view=changes&from=${encodeURIComponent(`cp:${checkpoints[0].id}`)}`);
              }}
            >
              최근 체크포인트와 지금 비교…
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-caption font-normal text-muted-foreground">
              위험한 편집 전에 저장하세요. ⌘Z로 되돌릴 수 없는 범위를 스냅샷으로 복구합니다.
            </DropdownMenuLabel>
          </DropdownMenuContent>
        </DropdownMenu>
        </ToolbarGroup>
      </div>

      {/* 7. 우측 유틸리티: 패널, 더보기 (사이드바가 닫혀 있을 때만 노출) */}
      {!ui.sidePanel && (
      <div className="ml-1.5 flex h-8 shrink-0 items-center gap-1.5 border-l border-border/60 pl-1.5">
          <ToolbarGroup>
            <TB
              icon={<PanelRight16Regular />}
              label={focus ? "인스펙터 열기" : "인사이트 열기"}
              shortcut={`${modKey}I`}
              active={false}
              onClick={() => s().setUI({ sidePanel: focus ? "inspector" : "insights" })}
            />

            {/* more */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex size-7 items-center justify-center rounded-[6px] text-foreground/80 transition-colors hover:bg-card hover:text-foreground hover:shadow-xs"
                  aria-label="보기 및 더보기 설정"
                >
                  <MoreHorizontal16Regular />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>보기</DropdownMenuLabel>
              <DropdownMenuCheckboxItem checked={ui.showCritical} onCheckedChange={(v) => s().setUI({ showCritical: !!v })}>
                여유 없는 작업 강조 (빨간색)
              </DropdownMenuCheckboxItem>
              {ui.scheduleView === "gantt" && (
                <>
                  <DropdownMenuCheckboxItem checked={ui.showDependencies} onCheckedChange={(v) => s().setUI({ showDependencies: !!v })}>
                    종속 관계선
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem checked={ui.highlightDrivingPath} onCheckedChange={(v) => s().setUI({ highlightDrivingPath: !!v })}>
                    선택 작업의 날짜를 정한 경로 강조
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem checked={ui.showProgressLine} onCheckedChange={(v) => s().setUI({ showProgressLine: !!v })}>
                    진척선
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem checked={ui.showFloat} onCheckedChange={(v) => s().setUI({ showFloat: !!v })}>
                    여유 표시
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem checked={ui.showProgressLabel} onCheckedChange={(v) => s().setUI({ showProgressLabel: !!v })}>
                    막대 안 진척률
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem checked={ui.showRemainingLabel} onCheckedChange={(v) => s().setUI({ showRemainingLabel: !!v })}>
                    단계 남은 기간
                  </DropdownMenuCheckboxItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => s().setCollapsedAll(true)}>모두 접기</DropdownMenuItem>
              <DropdownMenuItem onClick={() => s().setCollapsedAll(false)}>모두 펼치기</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => useSettingsHub.getState().open("app")}>
                앱 설정
                <span className="ml-auto text-caption text-muted-foreground">{modKey},</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => useSettingsHub.getState().open("app", "shortcuts")}>
                단축키
                <span className="ml-auto text-caption text-muted-foreground">{modKey}/</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {surface.exportPlan && (
                <>
                  <DropdownMenuItem
                    disabled={exportingExcel}
                    onClick={async () => {
                      setExportingExcel(true);
                      try {
                        const state = s();
                        await exportScheduleExcel(state.project, state.derived.schedule);
                      } finally {
                        setExportingExcel(false);
                      }
                    }}
                  >
                    {exportingExcel ? "Excel 생성 중…" : "Excel 내보내기"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => void saveText(`${project.name}.json`, s().exportProject())}>JSON 내보내기</DropdownMenuItem>
                </>
              )}
              {(surface.editWbs || surface.editSchedule) && (
                <DropdownMenuItem onClick={() => fileRef.current?.click()}>JSON 가져오기…</DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  const id = s().loadSample();
                  navigate(`/projects/${id}/schedule`);
                }}
              >
                샘플 플랜(ERP 구축) 추가…
              </DropdownMenuItem>
              {allowCreate && <DropdownMenuItem onClick={() => s().openDialog("create-plan")}>새 플랜…</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          </ToolbarGroup>
      </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="application/json"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (f) {
            const text = await f.text();
            if (s().importProject(text)) {
              const id = useProjectStore.getState().activeId;
              if (id) navigate(`/projects/${id}/schedule`);
            }
          }
          e.target.value = "";
        }}
      />

      <BaselineSaveDialog open={blDialog} onClose={() => setBlDialog(false)} />
      <BaselineManageDialog open={blManage} onClose={() => setBlManage(false)} />
      <CheckpointDialog mode={cpDialog} onClose={() => setCpDialog(null)} />
    </div>
  );
}

function ToolbarGroup({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex h-8 items-center rounded-lg bg-muted/60 p-0.5 gap-0.5 shrink-0", className)}>
      {children}
    </div>
  );
}

function TB({
  icon,
  label,
  shortcut,
  onClick,
  disabled,
  active,
}: {
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          onClick={onClick}
          aria-label={label}
          className={cn(
            "flex size-7 items-center justify-center rounded-[6px] text-foreground/80 transition-colors outline-none",
            "hover:bg-card hover:text-foreground hover:shadow-xs",
            "focus-visible:ring-2 focus-visible:ring-ring/50",
            "disabled:pointer-events-none disabled:opacity-40",
            "[&_svg]:size-[16px]",
            active && "bg-tint/15 text-tint font-normal shadow-xs",
          )}
        >
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-caption">
        {label}
        {shortcut && <span className="ml-2 font-mono text-muted-foreground">{shortcut}</span>}
      </TooltipContent>
    </Tooltip>
  );
}

function CheckpointDialog({ mode, onClose }: { mode: "save" | "restore" | null; onClose: () => void }) {
  const navigate = useNavigate();
  const checkpoints = useProjectStore((s) => s.checkpoints);
  const [name, setName] = useState("");
  const [keepProgress, setKeepProgress] = useState(true);
  const save = mode === "save";
  return (
    <AppDialog
      open={!!mode}
      onClose={onClose}
      title={save ? "체크포인트 저장" : "체크포인트에서 복원"}
      description={save ? "플랜 전체의 스냅샷을 브라우저에 저장합니다. 최대 20개를 유지합니다." : "복원은 실행 취소 스택에 기록되므로 ⌘Z로 되돌릴 수 있습니다."}
      width="md"
      footer={
        save ? (
          <DialogActionsBar
            onCancel={onClose}
            confirmLabel="저장"
            onConfirm={() => {
              useProjectStore.getState().saveCheckpoint(name);
              setName("");
              onClose();
            }}
          />
        ) : undefined
      }
    >
      {save ? (
        <Input placeholder={`체크포인트 ${checkpoints.length + 1}`} value={name} onChange={(e) => setName(e.target.value)} />
      ) : (
        <>
            <label className="flex items-center gap-2 text-body">
              <Checkbox checked={keepProgress} onCheckedChange={(v) => setKeepProgress(v === true)} />
              현재 진척률은 보존
            </label>
            <ul className="max-h-64 divide-y divide-border overflow-auto rounded-md border border-border">
              {checkpoints.map((c) => (
                <li key={c.id} className="flex items-center gap-2 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{c.name}</div>
                    <div className="text-caption text-muted-foreground">
                      {formatDate(c.createdAt, "dateTime")} · {c.project.tasks.length}개 작업
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 text-caption"
                    onClick={() => {
                      useProjectStore.getState().restoreCheckpoint(c.id, keepProgress);
                      onClose();
                    }}
                  >
                    복원
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-caption"
                    title="이 스냅샷을 현황의 보고 기간으로 저장합니다"
                    onClick={() => {
                      const st = useProjectStore.getState();
                      if (st.promoteCheckpointToReport(c.id) && st.activeId) {
                        onClose();
                        navigate(`/projects/${st.activeId}/reports?view=trend`);
                      }
                    }}
                  >
                    보고 기간으로
                  </Button>
                  <Button size="icon-sm" variant="ghost" className="size-6" onClick={() => useProjectStore.getState().deleteCheckpoint(c.id)} aria-label="삭제">
                    <Delete16Regular className="size-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
        </>
      )}
    </AppDialog>
  );
}

